<template>
  <main-layout>
    <div
      class="relative pt-16 pb-20 px-4 sm:px-8 lg:pt-24 lg:pb-28 lg:px-12 min-h-screen"
    >
      <div class="absolute inset-0">
        <div class="h-1/3 sm:h-2/3"></div>
      </div>
      <div class="relative max-w-7xl mx-auto">
        <div class="text-center">
          <h2
            class="text-3xl leading-9 tracking-tight font-extrabold text-gray-900 sm:text-4xl sm:leading-10"
          >
            Remote Jobs
          </h2>
          <p
            class="my-4 max-w-2xl mx-auto text-xl leading-7 text-gray-500 sm:mt-4"
          >
            The best remote tech jobs from multiple job resources in one place.
            Click on the tags to filter or start typing to search.
          </p>
          <input
            aria-label="Search"
            type="text"
            ref="search"
            v-debounce:300ms="searchJobs"
            class="mt-4 appearance-none w-full px-4 py-2 border border-gray-300 text-base leading-6 rounded-md text-gray-900 bg-white placeholder-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out lg:max-w-xs"
            placeholder="Search on position, company or tag"
          />
        </div>

        <section v-if="errored">
          <p>
            We're sorry, we're not able to retrieve this information at the
            moment, please try back later
          </p>
        </section>

        <section
          v-if="isFiltering"
          class="fixed top-5 rounded-lg shadow bg-white px-6 py-3"
        >
          <span class="font-bold">Filters:</span>
          <span
            v-for="(tag, i) in filterTags"
            v-bind:key="i"
            v-on:click="removeFilterTag(tag)"
            class="cursor-pointer mr-1 px-4 py-1 rounded-full text-sm font-lg leading-5 bg-indigo-100 text-indigo-800"
          >
            {{ tag }}
            <span>x</span>
          </span>
        </section>

        <div class="flex justify-end mt-4">
          <div class="w-6 h-6 mr-2">
            <svg
              v-on:click="isListView = false"
              v-bind:fill="isListView ? '#d2d6dc' : '#161e2e'"
              class="block cursor-pointer"
              viewBox="0 0 100 100"
              style="enable-background:new 0 0 100 100;"
            >
              <g>
                <path
                  d="M9.1,42.4h26.6c3.6,0,6.5-2.9,6.5-6.5V9.3c0-3.6-2.9-6.5-6.5-6.5H9.1c-3.6,0-6.6,2.9-6.6,6.5v26.5
		C2.5,39.4,5.5,42.4,9.1,42.4z M8,9.3c0-0.6,0.5-1,1.1-1h26.6c0.6,0,1,0.5,1,1v26.6c0,0.4-0.4,1-1,1H9.1c-0.6,0-1.1-0.5-1.1-1.1V9.3
		z"
                />
                <path
                  d="M64.1,42.4h26.5c3.5,0,6.5-2.8,6.6-6.3l0-26.7c0-3.6-2.9-6.5-6.5-6.5H64.1c-3.6,0-6.5,2.9-6.5,6.5v26.5
		C57.5,39.4,60.5,42.4,64.1,42.4z M63,9.3c0-0.6,0.5-1,1-1h26.6c0.6,0,1,0.5,1,1v26.5c-0.1,0.5-0.5,1-1.2,1H64.1c-0.6,0-1-0.5-1-1.1
		V9.3z"
                />
                <path
                  d="M35.9,57.7H9.3c-3.6,0-6.5,2.9-6.5,6.5v26.5c0,3.6,2.9,6.5,6.5,6.5h26.5c3.5,0,6.5-2.8,6.6-6.3l0-26.7
		C42.5,60.6,39.5,57.7,35.9,57.7z M37,90.7c-0.1,0.5-0.5,1-1.2,1H9.3c-0.6,0-1-0.5-1-1V64.2c0-0.6,0.5-1,1-1h26.6
		c0.6,0,1.1,0.5,1.1,1V90.7z"
                />
                <path
                  d="M90.8,57.7H64.2c-3.6,0-6.5,2.9-6.5,6.5v26.5c0,3.6,2.9,6.5,6.5,6.5h26.6c3.4,0,6.4-2.8,6.5-6.3l0-26.7
		C97.3,60.6,94.4,57.7,90.8,57.7z M91.8,90.7c-0.1,0.5-0.6,1-1,1H64.2c-0.6,0-1-0.5-1-1V64.2c0-0.6,0.5-1,1-1h26.6c0.6,0,1,0.5,1,1
		V90.7z"
                />
              </g>
            </svg>
          </div>
          <div class="w-6 h-6">
            <svg
              v-on:click="isListView = true"
              v-bind:fill="isListView ? '#161e2e' : '#d2d6dc'"
              class="block cursor-pointer"
              viewBox="0 0 100 100"
              style="enable-background:new 0 0 100 100;"
              xml:space="preserve"
            >
              <g>
                <path
                  d="M5.5,31.7h89c1.5,0,2.8-1.2,2.8-2.8s-1.2-2.8-2.8-2.8h-89c-1.5,0-2.8,1.2-2.8,2.8S4,31.7,5.5,31.7z"
                />
                <path
                  d="M94.5,47.3h-89c-1.5,0-2.8,1.2-2.8,2.8s1.2,2.8,2.8,2.8h89c1.5,0,2.8-1.2,2.8-2.8S96,47.3,94.5,47.3z"
                />
                <path
                  d="M94.5,68.3h-89c-1.5,0-2.8,1.2-2.8,2.8s1.2,2.8,2.8,2.8h89c1.5,0,2.8-1.2,2.8-2.8S96,68.3,94.5,68.3z"
                />
              </g>
            </svg>
          </div>
        </div>

        <section v-if="!errored">
          <job-list
            v-if="isListView"
            v-bind:open-modal="setSelectedJob"
            v-bind:jobs="isSearching ? searchedJobs : showingJobs"
          ></job-list>
          <job-cards
            v-else
            v-bind:open-modal="setSelectedJob"
            v-bind:jobs="isSearching ? searchedJobs : showingJobs"
          ></job-cards>

          <div
            v-if="isLoading"
            class="mt-12 mx-auto loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-20 w-20"
          ></div>
        </section>
        <div class="flex my-12" v-if="!isSearching && !isLoading">
          <button
            v-on:click="loadMore"
            class="mx-auto inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-500 focus:outline-none focus:shadow-outline-gray focus:border-gray-700 active:bg-gray-700"
          >
            Load more
          </button>
        </div>
      </div>
    </div>
    <job-modal
      v-bind:is-open="!!selectedJob"
      v-bind:job="selectedJob"
      v-bind:close-modal="() => setSelectedJob(null)"
    ></job-modal>
  </main-layout>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import marked from 'marked';
import relativeTime from 'dayjs/plugin/relativeTime';
import MainLayout from '../layouts/Main.vue';
import JobModal from './JobModal.vue';
import JobList from './JobList.vue';
import JobCards from './JobCards.vue';
import { promotedJobs } from './promotedJobs';

export default {
  name: 'JobPage',
  components: {
    JobModal,
    JobList,
    JobCards,
    MainLayout,
  },
  data() {
    return {
      showingJobs: [],
      filteredJobs: [],
      searchedJobs: [],
      allJobs: [],
      isLoading: false,
      isFiltering: false,
      filterTags: [],
      isSearching: false,
      isListView: true,
      errored: false,
      limit: 30,
      selectedJob: null,
      tag: null,
    };
  },

  methods: {
    searchJobs(searchText) {
      /* eslint-disable no-debugger */
      if (searchText) {
        this.isSearching = true;
        this.searchedJobs = this.allJobs.filter((job) => {
          return (
            job.position.toLowerCase().includes(searchText.toLowerCase()) ||
            job.companyName.toLowerCase().includes(searchText.toLowerCase()) ||
            job?.tags.some((tag) => searchText.toLowerCase().includes(tag))
          );
        });
      } else {
        this.isSearching = false;
      }
    },
    setSelectedJob(e, job) {
      if (e?.target?.className?.includes('tag')) {
        return;
      }
      this.selectedJob = job;
    },
    marked: function(input) {
      return marked(input);
    },
    loadMore() {
      this.isLoading = true;
      this.limit = this.limit + 30;
      this.showingJobs = this.allJobs.slice(
        0,
        this.limit || this.allJobs.length
      );
      this.isLoading = false;
    },
    addFilterTag(tag) {
      this.selectedJob = null;
      this.isSearching = false;
      this.filterTags.push(tag);
      this.filterJobs(this.filterTags);
    },
    removeFilterTag(tag) {
      this.filterTags = this.filterTags.filter(
        (filterTag) => filterTag !== tag
      );

      if (this.filterTags.length) {
        this.filterJobs(this.filterTags);
      } else {
        const slicedData = this.allJobs.slice(
          0,
          this.limit || this.allJobs.length
        );
        this.showingJobs = slicedData;
        this.isFiltering = false;
      }
    },
    filterJobs(tags) {
      this.showingJobs = this.allJobs.filter((job) =>
        job.tags.some((tag) => tags.includes(tag))
      );
      this.isFiltering = true;
    },
    getJobs(source) {
      return axios
        .get(`data/${source}.json`)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.log(error);
          this.isLoading = false;
          this.errored = true;
        });
    },
  },
  created() {
    dayjs.extend(relativeTime);
  },
  filters: {
    diffForHumans: (date) => {
      if (!date) {
        return null;
      }

      return dayjs(date).fromNow();
    },
  },
  async mounted() {
    // this.$refs.search.focus();

    if (!this.allJobs.length) {
      this.isLoading = true;
      const remoteokJobs = await this.getJobs('remoteok');
      const remotiveJobs = await this.getJobs('remotive');
      const findworkJobs = await this.getJobs('findwork');
      const mergedJobs = [...remoteokJobs, ...remotiveJobs, ...findworkJobs];
      const sortedJobs = mergedJobs.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      this.allJobs = sortedJobs;
      const slicedJobs = sortedJobs.slice(0, this.limit || sortedJobs.length);
      this.showingJobs = [...promotedJobs, ...slicedJobs];
      this.isLoading = false;
    }

    this.$root.$on('emitAddFilterTag', (tag) => {
      this.addFilterTag(tag);
    });
  },
};
</script>
<style scoped>
ul,
li {
  margin: 0;
}
.loader {
  border-top-color: #4b5563;
  -webkit-animation: spinner 1.5s linear infinite;
  animation: spinner 1.5s linear infinite;
}

@-webkit-keyframes spinner {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spinner {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
